import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  Renderer2,
  Inject,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomDatePickerComponent } from '../../../components/custom-date-picker/custom-date-picker.component';
import { ModalFooterActionsComponent } from '../modal-footer-actions/modal-footer-actions.component';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export interface PeriodOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomDatePickerComponent, ModalFooterActionsComponent],
  templateUrl: './date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./date-range-picker.component.scss'],
})
export class DateRangePickerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() selectedPeriod = 'Today';
  @Input() customDateRange: DateRange = { startDate: null, endDate: null };
  @Input() applyButtonLabel = 'Apply';
  /**
   * Export pickers (CWS-1637): Apply re-runs the action, so an unchanged custom range must
   * stay applicable — the user downloads the same dates again.
   */
  @Input() allowReapply = false;
  @Input() periodOptions: PeriodOption[] = [
    { label: 'Today', value: 'Today' },
    { label: 'Yesterday', value: 'Yesterday' },
    { label: 'Last 7 days', value: 'Last 7 days' },
    { label: 'Last 30 days', value: 'Last 30 days' },
    { label: 'This month', value: 'This month' },
    { label: 'Last month', value: 'Last month' },
    { label: 'This year', value: 'This year' },
    { label: 'Custom', value: 'Custom' },
  ];

  @Output() periodChange = new EventEmitter<string>();
  @Output() customDateRangeChange = new EventEmitter<DateRange>();
  @Output() close = new EventEmitter<void>();
  @Output() apply = new EventEmitter<{ period: string; customRange?: DateRange }>();

  @ViewChild('dropdownMenu') dropdownMenu!: ElementRef<HTMLElement>;
  @ViewChild('dropdownOptions') dropdownOptions!: ElementRef<HTMLElement>;
  @ViewChild('customDateRange') customDateRangeElement!: ElementRef<HTMLElement>;
  @ViewChild('mobileBackdrop') mobileBackdrop!: ElementRef<HTMLElement>;

  selectedOption = 'Today';
  tempCustomRange: DateRange = { startDate: null, endDate: null };
  dateRangeError: string | null = null;
  /** Footer visible only for Custom (draft-until-Apply) period selection. */
  showCustomFooter = false;
  /** Apply stays disabled until the draft differs from the applied baseline and is valid. */
  primaryDisabled = true;
  private isApplying = false;
  private isMobile = false;

  constructor(
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth <= 768;
    }
  }

  ngOnInit(): void {
    this.resetDraftFromApplied();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedPeriod']) {
      this.selectedOption = this.selectedPeriod;
    }

    if (changes['customDateRange']) {
      this.syncTempCustomRangeFromApplied();
      this.validateDateRange();
    }

    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue) {
        this.resetDraftFromApplied();
        if (this.showCustomFooter) {
          this.scrollCustomRangeIntoView();
        }
      }

      if (isPlatformBrowser(this.platformId)) {
        this.isMobile = window.innerWidth <= 768;

        if (this.isMobile) {
          if (changes['isOpen'].currentValue) {
            this.moveToBody();
            document.body.style.overflow = 'hidden';
          } else {
            document.body.style.overflow = '';
          }
        }
      }
    } else if (changes['selectedPeriod'] || changes['customDateRange']) {
      this.refreshFooterState();
    }
  }

  private resetDraftFromApplied(): void {
    this.isApplying = false;
    this.selectedOption = this.selectedPeriod;
    this.syncTempCustomRangeFromApplied();
    this.validateDateRange();
    this.refreshFooterState();
  }

  private syncTempCustomRangeFromApplied(): void {
    this.tempCustomRange = {
      startDate: this.customDateRange.startDate ? new Date(this.customDateRange.startDate) : null,
      endDate: this.customDateRange.endDate ? new Date(this.customDateRange.endDate) : null,
    };
  }

  private refreshFooterState(): void {
    this.showCustomFooter = this.selectedOption === 'Custom';
    const isCustomRangeValid =
      this.tempCustomRange.startDate !== null &&
      this.tempCustomRange.endDate !== null &&
      !this.dateRangeError;
    this.primaryDisabled =
      this.isApplying ||
      !this.showCustomFooter ||
      !isCustomRangeValid ||
      (!this.allowReapply && !this.hasDraftChanges());
  }

  private hasDraftChanges(): boolean {
    if (this.selectedOption !== this.selectedPeriod) {
      return true;
    }
    if (this.selectedOption !== 'Custom') {
      return false;
    }
    return (
      this.toDayKey(this.tempCustomRange.startDate) !==
        this.toDayKey(this.customDateRange.startDate) ||
      this.toDayKey(this.tempCustomRange.endDate) !== this.toDayKey(this.customDateRange.endDate)
    );
  }

  private toDayKey(date: Date | null): number | null {
    if (!date) {
      return null;
    }
    return this.toDayTime(date instanceof Date ? date : new Date(date));
  }

  private moveToBody(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => {
      if (this.dropdownMenu && this.mobileBackdrop && this.isMobile) {
        this.renderer.appendChild(document.body, this.mobileBackdrop.nativeElement);
        this.renderer.appendChild(document.body, this.dropdownMenu.nativeElement);
      }
    }, 0);
  }

  isCustomSelected(): boolean {
    return this.selectedOption === 'Custom';
  }

  onPeriodSelect(value: string): void {
    this.selectedOption = value;

    // Custom is draft-only until Apply; preset periods apply immediately.
    if (value !== 'Custom') {
      this.dateRangeError = null;
      this.refreshFooterState();
      this.periodChange.emit(value);
      this.apply.emit({ period: value });
      this.close.emit();
      return;
    }

    this.validateDateRange();
    this.refreshFooterState();
    this.scrollCustomRangeIntoView();
  }

  onStartDateChange(date: Date | null): void {
    if (date) {
      this.tempCustomRange.startDate = date instanceof Date ? date : new Date(date);
      if (
        this.tempCustomRange.endDate &&
        this.toDayTime(this.tempCustomRange.startDate) >
          this.toDayTime(this.tempCustomRange.endDate)
      ) {
        // Keep range valid when start is moved after the current end.
        this.tempCustomRange.endDate = null;
      }
    } else {
      this.tempCustomRange.startDate = null;
    }
    this.validateDateRange();
    this.refreshFooterState();
  }

  onEndDateChange(date: Date | null): void {
    if (date) {
      this.tempCustomRange.endDate = date instanceof Date ? date : new Date(date);
    } else {
      this.tempCustomRange.endDate = null;
    }
    this.validateDateRange();
    this.refreshFooterState();
  }

  onClear(): void {
    this.tempCustomRange = { startDate: null, endDate: null };
    this.dateRangeError = null;
    this.refreshFooterState();
  }

  onCancel(): void {
    this.resetDraftFromApplied();
    this.close.emit();
  }

  onApply(): void {
    if (this.primaryDisabled || this.isApplying) {
      return;
    }

    this.isApplying = true;
    this.refreshFooterState();

    const customRange = this.showCustomFooter ? { ...this.tempCustomRange } : undefined;
    if (customRange) {
      this.customDateRangeChange.emit(customRange);
    }
    this.apply.emit({
      period: this.selectedOption,
      customRange,
    });
    this.close.emit();
  }

  private validateDateRange(): void {
    const { startDate, endDate } = this.tempCustomRange;
    if (startDate && endDate && this.toDayTime(startDate) > this.toDayTime(endDate)) {
      this.dateRangeError = 'End date must be on or after the start date';
      return;
    }
    this.dateRangeError = null;
  }

  private toDayTime(date: Date): number {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized.getTime();
  }

  private scrollCustomRangeIntoView(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Wait for *ngIf custom section to render, then bring it into view.
    setTimeout(() => {
      const menu = this.dropdownMenu?.nativeElement;
      const customEl = this.customDateRangeElement?.nativeElement;
      if (!menu || !customEl) {
        return;
      }

      customEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

      // Prefer scrolling inside the dropdown when the menu itself is scrollable (mobile).
      const menuRect = menu.getBoundingClientRect();
      const customRect = customEl.getBoundingClientRect();
      if (customRect.bottom > menuRect.bottom || customRect.top < menuRect.top) {
        const delta =
          customRect.bottom > menuRect.bottom
            ? customRect.bottom - menuRect.bottom + 12
            : customRect.top - menuRect.top - 12;
        menu.scrollBy({ top: delta, behavior: 'smooth' });
      }
    }, 0);
  }

  ngOnDestroy(): void {
    // Ensure body scroll is restored when component is destroyed
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
